import type { CSSProperties } from 'react'
import {
  getDivisionWorkspace,
  DIVISION_WORKSPACES,
  type DivisionCard,
} from './divisionWorkspaces'
import { CommandCenterShell } from '../shell'
import './division-workspace.css'

/**
 * DivisionWorkspaceShell — the generic V2 division workspace surface.
 *
 * PHASE_2G: renders inside the shared CommandCenterShell (same Q home / back /
 * breadcrumb / wide frame / top-nav bar as every other V2 surface). CHAT #6
 * conversion: the division content plane is the shared `.sqv-surface` and all
 * presentation moved to sq-v2-surface.css + division-workspace.css. The only
 * inline style is the DYNAMIC per-division accent (--sqv-accent). Behavior is
 * unchanged: each card LINKS to an existing registered route (kind 'route') or
 * honestly marks not-yet-wired functionality (kind 'planned' / 'setup_required')
 * with a status chip that explains why it is disabled. Unknown/`all` renders the
 * shell + division bar as a picker (no dead end).
 *
 * Presentational + decoupled + SSR-safe.
 */
export interface DivisionWorkspaceShellProps {
  divisionId?: string
  /** Navigate to a card's registered route. Wired by the route to real navigation. */
  onOpenRoute?: (to: string) => void
  /** Back to the V2 command center (/square/command-center). */
  onBack?: () => void
  /** Switch the active division (top bar / picker) — routes to /workspaces/:id. */
  onOpenDivision?: (id: string) => void
  /** Q home — returns to the main Square dashboard (/square/dashboard). */
  onHome?: () => void
}

const CHIP_STATUS: Record<DivisionCard['kind'], { status: string; label: string }> = {
  route: { status: 'open', label: 'OPEN' },
  setup_required: { status: 'setup', label: 'SETUP REQUIRED' },
  planned: { status: 'planned', label: 'PLANNED' },
}

function StatusChip({ card }: { card: DivisionCard }) {
  const meta = CHIP_STATUS[card.kind]
  return (
    <span className="sqv-chip" data-status={meta.status}>{meta.label}</span>
  )
}

function Card({ card, accent, onOpenRoute }: { card: DivisionCard; accent: string; onOpenRoute?: (to: string) => void }) {
  const isRoute = card.kind === 'route'
  const open = () => {
    if (isRoute && card.to) onOpenRoute?.(card.to)
  }
  return (
    <button
      type="button"
      onClick={open}
      aria-disabled={!isRoute}
      className="sqv-card sqv-bloom sqv-dw-card"
      data-interactive={isRoute}
      style={{ ['--sqv-accent']: accent } as CSSProperties}
    >
      <div className="sqv-dw-card__head">
        <strong className="sqv-dw-card__title">{card.label}</strong>
        <StatusChip card={card} />
      </div>
      <p className="sqv-dw-card__summary">{card.summary}</p>
      {isRoute && <span className="sqv-dw-card__open">Open →</span>}
    </button>
  )
}

export default function DivisionWorkspaceShell({ divisionId, onOpenRoute, onBack, onOpenDivision, onHome }: DivisionWorkspaceShellProps) {
  const division = getDivisionWorkspace(divisionId)

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBack}
      breadcrumb={division ? ['COMMAND CENTER', 'WORKSPACES', division.label.toUpperCase()] : ['COMMAND CENTER', 'WORKSPACES']}
      breadcrumbSeparator="·"
      title={division ? division.label : 'Division Workspaces'}
      titleAccent={division ? division.accent : undefined}
      titleDot={!!division}
      honestyBadge={{
        label: 'LINKS · NOT FAKED',
        title: 'This workspace links to existing Square Code surfaces. OPEN cards open a real route; PLANNED and SETUP-REQUIRED cards are honest placeholders — nothing here is faked.',
      }}
      topNav={{
        items: DIVISION_WORKSPACES.map((d) => ({ id: d.id, label: d.label, accent: d.accent })),
        activeId: division?.id,
        onSelect: (id) => onOpenDivision?.(id),
      }}
    >
      <div className="sqv-surface sqv-atmosphere sqv-dw">
        {division ? (
          <div>
            <p className="sqv-dw__tagline">{division.tagline}</p>
            <div className="sqv-grid">
              {division.cards.map((card) => (
                <Card key={card.label} card={card} accent={division.accent} onOpenRoute={onOpenRoute} />
              ))}
            </div>
          </div>
        ) : (
          <div className="sqv-dw-picker">
            <div className="sqv-dw-picker__title">Division Workspaces</div>
            <p className="sqv-dw-picker__body">
              Pick a division from the bar above to open its workspace — each one links to real Square Code surfaces
              (or shows honest planned / setup-required states).
            </p>
          </div>
        )}
      </div>
    </CommandCenterShell>
  )
}
